import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import { TwoGisAutocomplete } from '@douleutarasgr/2gis-plugin';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { twoGisAutocompleteOptions } from 'Douleutaras/configuration/twoGis';
import { createGisFromTwoGis } from 'Douleutaras/components/Forms/twoGisModel';

import './TwoGisAutocompleteField.scss';

interface TwoGisAutocompleteFieldProps {
  input: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
    >;
  children?: React.ReactElement;
  onChange(gis: Gis): void;
}

// Reads the `.description` off a value that may be a plain string or a Gis instance,
// mirroring GoogleAutocompleteField's handling of `input.value`.
const resolveDisplayValue = (value: React.InputHTMLAttributes<HTMLInputElement>['value']): string => {
  if (value && typeof value === 'object') {
    return (value as unknown as { description?: string }).description || '';
  }

  return value === undefined ? '' : `${value}`;
};

/**
 * 2GIS counterpart of {@link GoogleAutocompleteField}. Renders the same input (either
 * its own or a cloned child, e.g., CustomInput) and attaches 2GIS autocomplete to the
 * underlying DOM input. Selecting a suggestion emits a provider-agnostic {@link Gis}.
 */
export const TwoGisAutocompleteField = ({ input, children, onChange }: TwoGisAutocompleteFieldProps) => {
  // tslint:disable-next-line:no-null-keyword
  const wrapperRef = useRef<HTMLDivElement>(null);
  // 2GIS controls the DOM input imperatively, so the input must stay uncontrolled:
  // `value` seeds the display text (via defaultValue + the effect below) but is never
  // spread onto the input, which would hand control to React and fight 2GIS's writes.
  const { value: inputValue, ...inputProps } = input;
  const [displayValue, setDisplayValue] = useState(resolveDisplayValue(inputValue));

  useEffect(() => {
    const inputElement = wrapperRef.current?.querySelector('input');
    if (!inputElement) {
      return;
    }

    const autocomplete = new TwoGisAutocomplete(inputElement, twoGisAutocompleteOptions);
    const subscription: Subscription = autocomplete.changed.subscribe(suggest => {
      const gis = createGisFromTwoGis([suggest]);
      if (gis) {
        onChange(gis);
      }
    });

    return () => {
      subscription.unsubscribe();
      autocomplete.destroy();
    };
  }, []);

  // Reflect an externally provided value (e.g., the address picked on the map) into the
  // DOM input, which 2GIS controls imperatively.
  useEffect(() => {
    const value = resolveDisplayValue(inputValue);
    setDisplayValue(value);

    const inputElement = wrapperRef.current?.querySelector('input');
    if (inputElement) {
      inputElement.value = value;
    }
  }, [inputValue]);

  return (
    <div styleName="two-gis-autocomplete" ref={wrapperRef}>
      { children !== undefined
        ? cloneElement(children as React.ReactElement<{ input: unknown }>, {
            input: { ...inputProps, defaultValue: displayValue }
          })
        : <input className={inputProps.className}
                 name={inputProps.name}
                 placeholder={inputProps.placeholder}
                 defaultValue={displayValue}
        />
      }
    </div>
  );
};
